import {
	type ApiClient,
	createApiClient,
	createOptionsProxy,
	createQueryClient,
	type OptionsProxy,
} from "@nwga/features/client";
import type { QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { env } from "./env.ts";
import { routeTree } from "./routeTree.gen.ts";

/**
 * The router, and what every route can reach through its context.
 *
 * `trpc` here is the same options proxy `useTRPC()` gives a component, so a
 * loader can prefetch with `queryClient.ensureQueryData(context.trpc.x.queryOptions(…))`
 * and the component's `useQuery` then reads a cache that is already warm — same
 * query key, no second request, no spinner on navigation.
 */
export interface RouterContext {
	readonly queryClient: QueryClient;
	readonly api: ApiClient;
	readonly trpc: OptionsProxy;
}

export function createRouter() {
	const queryClient = createQueryClient();
	const api = createApiClient({
		baseUrl: env.apiUrl,
		development: env.isDevelopment,
	});
	const trpc = createOptionsProxy(api, queryClient);

	return createTanStackRouter({
		routeTree,
		context: { queryClient, api, trpc } satisfies RouterContext,
		// A pending state that flashes for 50ms is worse than none at all.
		defaultPendingMs: 300,
		defaultPendingMinMs: 400,
		defaultPreload: "intent",
		// Preloading is only useful if it populates the cache the component reads;
		// Query owns staleness, so the router should not second-guess it.
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
	});
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createRouter>;
	}
}
